import { useMemo } from 'react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { DetailCard, FieldRow } from '@/admin/components/shared/detail-card'
import { RegistrationConfiguredFieldRows } from '@/admin/components/registration-configured-fields'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import type {
  EntityTableDisplayColumn,
  EntityTypeDefinition,
} from '@/admin/hooks/use-entities'
import {
  isWorkflowStateResolution,
  resolveRegistrationDisplayColumnValue,
} from '@/admin/lib/entity-card-display'
import { buildRegistrationConfiguredFieldItems } from '@/admin/lib/registration-configured-fields'
import type { Holding } from '@/admin/types/products'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'
import { WorkflowStateBadge } from '@/shared/components/workflows'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { defineField, type FieldType } from '@/shared/lib/forms'

import { STATUS_WORKFLOW_KEY } from './display-preview-helpers'

const CARD_SECTIONS = new Set(['registration_card', 'purchase_card'])

/**
 * Walk a configured column's relationship path through the entity registry
 * to find the entity that actually owns ``attribute_code_name``, so the
 * preview can look up the field's ``field_type`` for display formatting.
 * Mirrors ``resolveTerminalEntityKey`` on the registrations list page.
 */
function resolveTerminalEntityKey(
  column: EntityTableDisplayColumn,
  entityTypes: EntityTypeDefinition[],
): string {
  const path = column.relationship_path?.length
    ? column.relationship_path
    : column.relationship_key
      ? [column.relationship_key]
      : []
  let currentKey =
    path.length === 0
      ? column.entity_key || 'holding'
      : path[0] === 'holdings' || path[0] === 'holding'
        ? 'host'
        : 'holding'
  for (const key of path) {
    const ent = entityTypes.find((e) => e.key === currentKey)
    const rel = ent?.relationships.find((r) => r.key === key)
    if (rel?.target_entity_key) {
      currentKey = rel.target_entity_key
    } else {
      currentKey =
        key === 'holdings' ? 'holding' : key === 'warranties' ? 'warranty' : key
    }
  }
  return currentKey
}

export function DisplayPreviewDialog({
  open,
  onOpenChange,
  entity,
  sectionKey,
  title,
  columns,
  holdings,
  entityTypes,
  previewHolding,
  locale,
  currency,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entity: EntityTypeDefinition
  sectionKey: string
  title: string
  columns: EntityTableDisplayColumn[]
  /** Sample registrations used for the table preview. */
  holdings: Holding[]
  entityTypes: EntityTypeDefinition[]
  /** Best-populated registration used for the card preview. */
  previewHolding: Holding | null
  locale?: string
  currency?: string
}) {
  const isCard = CARD_SECTIONS.has(sectionKey)

  const cardItems = useMemo(
    () =>
      buildRegistrationConfiguredFieldItems({
        holding: previewHolding,
        columns,
        entityTypes,
        statusWorkflowKey: STATUS_WORKFLOW_KEY,
        locale,
        currency,
      }),
    [columns, currency, entityTypes, locale, previewHolding],
  )

  const tableColumns = useMemo<DataTableColumn<Holding>[]>(
    () =>
      columns.map((column) => {
        const terminalEntityKey = resolveTerminalEntityKey(column, entityTypes)
        const attribute = entityTypes
          .find((e) => e.key === terminalEntityKey)
          ?.attributes.find((a) => a.code_name === column.attribute_code_name)

        // Workflow-state columns (Payment / Status) render a badge from the
        // resolved workflow state - the lifecycle status keeps its StatusBadge,
        // other workflow columns render a coloured WorkflowStateBadge.
        if (attribute?.configuration_data?.value_source === 'workflow_state') {
          const attrWorkflowKey =
            attribute.configuration_data.workflow_key ||
            column.attribute_code_name
          const isLifecycleStatus = attrWorkflowKey === STATUS_WORKFLOW_KEY
          return {
            id: column.id,
            header: column.label || attribute.display_name,
            cell: (row: Holding) => {
              const value = resolveRegistrationDisplayColumnValue(row, column, {
                statusWorkflowKey: STATUS_WORKFLOW_KEY,
                entityTypes,
              })
              if (!isLifecycleStatus && isWorkflowStateResolution(value)) {
                return (
                  <WorkflowStateBadge
                    workflowKey={value.workflowKey}
                    stateKey={value.stateKey}
                    label={value.label}
                    color={value.color}
                    isTerminal={value.isTerminal}
                  />
                )
              }
              return <StatusBadge status={String(value || '-')} />
            },
          }
        }
        const field = attribute
          ? defineField({
              code_name: attribute.code_name,
              display_name: attribute.display_name,
              field_type: attribute.field_type as FieldType,
              configuration_data: attribute.configuration_data,
            })
          : undefined
        return {
          id: column.id,
          header: column.label || column.attribute_code_name,
          field,
          getValue: (row: Holding) =>
            resolveRegistrationDisplayColumnValue(row, column, {
              statusWorkflowKey: STATUS_WORKFLOW_KEY,
              entityTypes,
            }),
        }
      }),
    [columns, entityTypes],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title} preview</DialogTitle>
          <DialogDescription>
            {isCard
              ? 'Live preview of this card on the registration details page, rendered with a real registration.'
              : 'Live preview of this table, rendered with your most recent registrations.'}
          </DialogDescription>
        </DialogHeader>

        {isCard ? (
          <DetailCard
            title={entity.label}
            icon={<LucideIconByName name={entity.icon} className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            {cardItems.length > 0 ? (
              <RegistrationConfiguredFieldRows items={cardItems} />
            ) : (
              <FieldRow label="No values" value="-" muted />
            )}
          </DetailCard>
        ) : (
          <DataTable
            tableId={`display-preview-${sectionKey}`}
            columns={tableColumns}
            data={holdings}
            rowKey="uuid"
            formatOptions={{ locale, currency }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
